import React, { useState } from 'react'
import axios from 'axios'
import { FormControlLabel, Radio, RadioGroup } from '@mui/material'
import Modal from '../../shared/modal'

export interface InvalidatableVoucher {
  id: number
  token: string
}

interface Props {
  voucher: InvalidatableVoucher
  onClose: () => void
  onInvalidated: (voucher: Record<string, unknown>) => void
}

const REASON_OPTIONS = [
  { value: 'customer_refund', label: 'Customer took a refund instead' },
  { value: 'admin', label: 'Administrative' }
]

const InvalidateVoucherModal: React.FC<Props> = ({ voucher, onClose, onInvalidated }) => {
  const [reason, setReason] = useState(REASON_OPTIONS[0].value)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setSubmitting(true)
    setError(null)
    axios
      .put(`/admin/vouchers/${voucher.id}/invalidate`, { invalidated_reason: reason, invalidation_note: note })
      .then(({ data }) => onInvalidated(data.voucher))
      .catch((err) => {
        setError(err.response?.data?.error || 'Could not invalidate the voucher.')
        setSubmitting(false)
      })
  }

  const footer = (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </button>
      <button type="button" className="btn btn-danger" onClick={submit} disabled={submitting}>
        {submitting ? 'Invalidating…' : 'Invalidate voucher'}
      </button>
    </>
  )

  return (
    <Modal headerText={`Invalidate voucher ${voucher.token}`} onClose={onClose} footerContent={footer} centered>
      {error && <div className="alert alert-danger">{error}</div>}

      <p>The customer will no longer be able to spend this voucher. Record why:</p>

      <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
        {REASON_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            label={option.label}
            control={<Radio sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }} />}
          />
        ))}
      </RadioGroup>

      <div className="form-group mt-3">
        <label htmlFor="voucher-invalidation-note">Note (optional)</label>
        <input
          id="voucher-invalidation-note"
          type="text"
          className="form-control"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. refunded at the desk, receipt 1234"
        />
      </div>
    </Modal>
  )
}

export default InvalidateVoucherModal
